import { useEffect, useRef } from "react";
import { TableHotkeyActions } from "../../types/types.hotkeys";

interface Props extends TableHotkeyActions {
  disabled?: boolean;
  isEditing?: boolean;
  focusFirstColumn?: () => void;
}

const RU_EN_KEY_MAP: Record<string, string> = {
  'ф': 'a', 'и': 'b', 'с': 'c', 'в': 'd', 'у': 'e', 'а': 'f', 'п': 'g',
  'р': 'h', 'ш': 'i', 'о': 'j', 'л': 'k', 'д': 'l', 'ь': 'm', 'т': 'n',
  'щ': 'o', 'з': 'p', 'й': 'q', 'к': 'r', 'ы': 's', 'е': 't', 'г': 'u',
  'м': 'v', 'ц': 'w', 'ч': 'x', 'н': 'y', 'я': 'z'
};

const normalizeKey = (key: string): string => {
  const lowerKey = key.toLowerCase();
  return RU_EN_KEY_MAP[lowerKey] || lowerKey;
};

export function useTableHotkeys(props: Props) {
  const { disabled = false, isEditing = false } = props;
  const handlersRef = useRef(props);
  const stateRef = useRef({ disabled, isEditing });

  useEffect(() => {
    handlersRef.current = props;
    stateRef.current = { disabled, isEditing };
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const rawKey = e.key?.toLowerCase();
      if (!rawKey) return;

      const key = normalizeKey(rawKey);
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      const alt = e.altKey;
      const target = e.target as HTMLElement;
      const isTab = key === "tab";

      const handlers = handlersRef.current;
      const state = stateRef.current;

      const tagName = target.tagName;
      const inputType = tagName === "INPUT" ? (target as HTMLInputElement).type : null;
      const isTextArea = tagName === "TEXTAREA";
      const isSelect = tagName === "SELECT";
      const isTextInput = inputType === "text";
      const isPasswordInput = inputType === "password";
      const isNumberInput = inputType === "number";
      const isCheckboxInput = inputType === "checkbox";
      const isColorInput = inputType === "color";
      const isDateInput = inputType === "date";
      const isDateTimeInput = inputType === "datetime-local";
      const isTimeInput = inputType === "time";
      const isFileInput = inputType === "file";
      
      const isButton = tagName === "BUTTON";
      const isInDropdown = target.closest('[class*="dropdown"]') !== null;
      const isDropdownMenu = isInDropdown;
      const isInputField = tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT" || target.isContentEditable;
      const hasRole = target.hasAttribute('role');
      const isCombobox = hasRole && target.getAttribute('role') === 'combobox';

      if (isTab && isInputField) {
        return; 
      }

      if (key === "f1") {
        e.preventDefault();
        handlers.onHelp?.();
        return;
      }

      if (state.disabled) return;

      if (ctrl && (key === "arrowup" || key === "arrowdown") && !shift && !alt) {
        e.preventDefault();
        key === "arrowup" 
          ? handlers.onSelectPrevRow?.() 
          : handlers.onSelectNextRow?.();
        return;
      }

      if (ctrl && shift && key === "e" && !alt && !state.isEditing) {
        e.preventDefault();
        handlers.onManageColumns?.();
        return;
      }

      if (alt && (key === "arrowleft" || key === "arrowright") && !state.isEditing) {
        e.preventDefault();
        key === "arrowleft" ? handlers.onBack?.() : handlers.onForward?.();
        return;
      }

      if (key === "f9" && !state.isEditing) {
        e.preventDefault();
        handlers.onOpenFilterModal?.();
        return;
      }

      if (state.isEditing || isInputField) {
        
        if (key === "tab" && !ctrl && !alt) {
          e.preventDefault();
          
          if (isSelect || isFileInput) return;

          shift
            ? handlers.onSelectPrevColumn?.()
            : handlers.onSelectNextColumn?.();
          return;
        }

        if (key === "escape" && !ctrl && !shift && !alt) {
          if (isDropdownMenu || isCombobox) return; 
          e.preventDefault();
          handlers.onCancelEdit?.();
          return;
        }

        if (ctrl && key === "enter" && !shift && !alt) {
          e.preventDefault();
          e.stopPropagation(); 
          handlers.onSaveAll?.();
          return;
        }

        if (key === "enter" && !ctrl && !shift && !alt) {
          if (isDropdownMenu || isCombobox) {
            const openMenu = document.querySelector('[class*="dropdown"][class*="open"], [class*="dropdown"][class*="show"]');
            if (openMenu) return;
          }
          
          if (isCheckboxInput) {
            e.preventDefault();
            (target as HTMLInputElement).click(); 
            return;
          }

          if (isButton) {
            e.preventDefault();
            target.click();
            return;
          }
          
          if (isSelect) {
            e.preventDefault();
            const selectElement = target as HTMLSelectElement;
            
            if (document.activeElement === selectElement) {
              selectElement.blur();
            }
            return;
          }
          
          if (isTextArea) return;
          
          if (isDateInput || isDateTimeInput || isTimeInput) {
            e.preventDefault();
            return;
          }
          
          if (isColorInput) {
            e.preventDefault();
            return;
          }
          
          if (isFileInput) {
            e.preventDefault();
            return;
          }
          
          if (isInputField) {
            e.preventDefault();
            return;
          }
        }

        if ((key === "arrowup" || key === "arrowdown") && !ctrl && !shift && !alt) {
          if (isDropdownMenu || isCombobox) {
            const openMenu = document.querySelector('[class*="dropdown"][class*="open"], [class*="dropdown"][class*="show"]');
            if (openMenu) return;
          }
          
          if (isSelect) return;
          
          if (isNumberInput) return;
          
          if (isTextArea) return; 
          
          if (isTextInput || isPasswordInput) {
            e.preventDefault();
            return;
          }
          
          e.preventDefault();
          return;
        }

        if ((key === "arrowleft" || key === "arrowright") && !ctrl && !shift && !alt) {
          if (isTextInput || isPasswordInput || isTextArea) return;
          
          if (isDateInput || isDateTimeInput || isTimeInput) return;
          
          e.preventDefault();
          return;
        }

        if (ctrl && key === "c" && !shift && !alt) {
          if (window.getSelection()?.toString()) return; 
          
          if (isInputField) {
            const element = target as HTMLInputElement | HTMLTextAreaElement;
            if (element.selectionStart !== element.selectionEnd) {
              return; 
            }
          }
          
          e.preventDefault();
          handlers.onCopy?.();
          return;
        }

        if (ctrl && key === "v" && !shift && !alt) {
          if (isTextInput || isPasswordInput || isTextArea || isNumberInput) {
            return;
          }
          
          e.preventDefault();
          handlers.onPaste?.();
          return;
        }

        if (ctrl && shift && key === "v" && !alt) {
          if (isTextInput || isPasswordInput || isTextArea || isNumberInput) {
            return;
          }
          
          e.preventDefault();
          handlers.onPasteValues?.();
          return;
        }

        if (alt && key === "n" && !ctrl && !shift) {
          e.preventDefault();
          handlers.onAddRow?.();
          return;
        }

        return;
      }
      
      if (alt && key === "n" && !ctrl && !shift) {
        e.preventDefault();
        handlers.onAddRow?.();
        return;
      }

      if (ctrl && key === "e" && !shift && !alt) {
        e.preventDefault();
        handlers.onEditRow?.();
        return;
      }

      if (ctrl && key === "d" && !shift && !alt) {
        e.preventDefault();
        handlers.onDeleteRow?.();
        return;
      }

      if (key === "pageup" && !ctrl && !shift && !alt) {
        e.preventDefault();
        handlers.onPrevPage?.();
        return;
      }

      if (key === "pagedown" && !ctrl && !shift && !alt) {
        e.preventDefault();
        handlers.onNextPage?.();
        return;
      }

      if (key === "home" && !ctrl && !shift && !alt) {
        e.preventDefault();
        handlers.onFirstPage?.();
        return;
      }

      if (key === "end" && !ctrl && !shift && !alt) {
        e.preventDefault();
        handlers.onLastPage?.();
        return;
      }

      if (ctrl && shift && key === "s" && !alt) {
        e.preventDefault();
        handlers.onSortColumn?.();
        return;
      }

      if (ctrl && key === "c" && !shift && !alt) {
        if (window.getSelection()?.toString()) return;
        e.preventDefault();
        handlers.onCopy?.();
        return;
      }

      if (ctrl && key === "v" && !shift && !alt) {
        if (window.getSelection()?.toString()) return;
        e.preventDefault();
        handlers.onPaste?.();
        return;
      }

      if (ctrl && shift && key === "v" && !alt) {
        if (window.getSelection()?.toString()) return;
        e.preventDefault();
        handlers.onPasteValues?.();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
}